import { useCallback, useEffect, useRef } from 'react';
import { Box, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useComputedColorScheme } from '@mantine/core';
import { formatPoints } from '../utils/format';
import { numStyle } from '../theme';

/**
 * Mapa nepracuje s API tvarem přímo — držitele stanoviště si volající dohledá sám
 * (server posílá jen last_holder_group_id) a předá ho už rozřešeného.
 */
export interface MapStation {
  id: number;
  position: number;
  label: string;
  points: number;
  holderName?: string | null;
  holderColor?: string | null;
}

/**
 * Situační mapa terénu: vrstevnice na canvasu, na nich stanoviště jako waypointy
 * obarvené podle skupiny, která je právě drží, spojené čárkovanou hlídkovou trasou.
 *
 * Stanoviště v databázi souřadnice nemají — rozmístění se odvozuje deterministicky
 * z pořadí, takže táž hra vypadá pokaždé stejně. Není to mapa skutečného terénu,
 * jen čitelný přehled „kdo kde je“.
 */

const HILLS = [
  { x: 0.17, y: 0.58, r: 0.055, k: 3, p: 0.9 },
  { x: 0.52, y: 0.24, r: 0.045, k: 4, p: 1.7 },
  { x: 0.83, y: 0.7, r: 0.05, k: 3, p: 2.6 },
  { x: 0.4, y: 0.9, r: 0.032, k: 5, p: 0.3 },
];

/** Rozprostře N stanovišť po ploše tak, aby se nepřekrývala a nelezla k okrajům. */
function stationPoint(index: number, total: number): { x: number; y: number } {
  const cols = Math.min(4, Math.max(2, Math.ceil(Math.sqrt(total))));
  const rows = Math.ceil(total / cols);
  const col = index % cols;
  const row = Math.floor(index / cols);
  // Liché řady posunuté, ať to nevypadá jako mřížka.
  const jitterX = row % 2 === 0 ? 0 : 0.5;
  const x = ((col + 0.5 + jitterX) / cols) * 0.78 + 0.11;
  const y = ((row + 0.5) / rows) * 0.62 + 0.24;
  return { x: Math.min(0.92, x) * 100, y: y * 100 };
}

interface Props {
  stations: MapStation[];
  height?: number;
  onStationClick?: (station: MapStation) => void;
}

export function SituationMap({ stations, height = 232, onStationClick }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scheme = useComputedColorScheme('light');

  const draw = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const w = cv.clientWidth;
    const h = cv.clientHeight;
    if (!w || !h) return;

    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = Math.round(w * dpr);
    cv.height = Math.round(h * dpr);
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const stroke =
      getComputedStyle(document.documentElement).getPropertyValue('--contour').trim() || '#B3A188';
    const M = Math.max(w, h);
    ctx.lineWidth = 1;
    ctx.strokeStyle = stroke;

    for (const hill of HILLS) {
      for (let ring = 0; ring < 9; ring++) {
        const alpha = 0.34 - ring * 0.032;
        if (alpha <= 0.02) continue;
        ctx.globalAlpha = alpha;
        const base = (hill.r + ring * 0.038) * M;
        ctx.beginPath();
        for (let a = 0; a <= Math.PI * 2 + 0.001; a += 0.05) {
          const wob =
            1 +
            0.11 * Math.sin(a * hill.k + hill.p) +
            0.055 * Math.sin(a * (hill.k + 3) - hill.p * 2) +
            0.03 * Math.sin(a * (hill.k + 7) + ring * 0.45);
          const r = base * wob;
          const x = hill.x * w + Math.cos(a) * r;
          const y = hill.y * h + Math.sin(a) * r * 0.6;
          if (a === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.stroke();
      }
    }

    // hlídková trasa mezi stanovišti
    if (stations.length > 1) {
      ctx.globalAlpha = 0.5;
      ctx.setLineDash([5, 5]);
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      stations.forEach((_, i) => {
        const p = stationPoint(i, stations.length);
        const x = (p.x / 100) * w;
        const y = (p.y / 100) * h;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.globalAlpha = 1;
  }, [stations]);

  useEffect(() => {
    draw();
    const cv = canvasRef.current;
    if (!cv || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => draw());
    ro.observe(cv);
    return () => ro.disconnect();
  }, [draw, scheme]);

  return (
    <Box
      style={{
        position: 'relative',
        height,
        background:
          'linear-gradient(170deg, var(--mantine-color-body), var(--mantine-color-default-hover))',
      }}
    >
      <canvas
        ref={canvasRef}
        aria-hidden
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      />
      {stations.map((station, i) => {
        const p = stationPoint(i, stations.length);
        const color = station.holderColor;
        const label = station.label;
        const tip = `${station.position}. ${label} · ${formatPoints(station.points)} b${
          station.holderName ? ` · drží ${station.holderName}` : ''
        }`;
        return (
          <Tooltip key={station.id} label={tip} withArrow>
            <UnstyledButton
              onClick={() => onStationClick?.(station)}
              style={{
                position: 'absolute',
                left: `${p.x}%`,
                top: `${p.y}%`,
                transform: 'translate(-50%,-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Box
                style={{
                  width: 31,
                  height: 31,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  background: 'var(--mantine-color-body)',
                  border: `2px ${color ? 'solid' : 'dashed'} ${
                    color || 'var(--mantine-color-dimmed)'
                  }`,
                  color: color || 'var(--mantine-color-dimmed)',
                  boxShadow: '0 2px 6px rgba(0,0,0,.18)',
                  ...numStyle,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {station.position}
              </Box>
              <Text
                visibleFrom="md"
                style={{
                  ...numStyle,
                  fontSize: 9.5,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  padding: '1px 5px',
                  borderRadius: 3,
                  background: 'var(--mantine-color-body)',
                }}
                c="dimmed"
              >
                {label}
              </Text>
            </UnstyledButton>
          </Tooltip>
        );
      })}
    </Box>
  );
}
